import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  BarChart3, Eye, MousePointer, Mail, Users,
  TrendingUp, TrendingDown, Loader2, RefreshCw
} from 'lucide-react';

import { API } from '@/utils/api';

export default function Statistics() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    views: { total: 0, trend: 0 },
    clicks: { total: 0, trend: 0 },
    inquiries: { total: 0, trend: 0 },
    leads: { total: 0, new: 0 }
  });
  const [period, setPeriod] = useState('7d');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/logowanie');
    } else if (user) {
      fetchStats();
    }
  }, [user, authLoading, navigate, period]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Get kennels first
      const kennelsRes = await axios.get(`${API}/kennels`, { headers });
      const kennels = kennelsRes.data;
      
      if (kennels.length > 0) {
        const metricsRes = await axios.get(`${API}/dashboard/metrics?kennel_id=${kennels[0].id}&period=${period}`, { headers }).catch(() => ({ data: {} }));
        const leadsRes = await axios.get(`${API}/stats`, { headers }).catch(() => ({ data: {} }));
        
        setStats({
          views: { 
            total: metricsRes.data?.views_last_7d || 0, 
            trend: metricsRes.data?.views_trend || 0 
          },
          clicks: { 
            total: metricsRes.data?.clicks || 0, 
            trend: metricsRes.data?.clicks_trend || 0 
          },
          inquiries: { 
            total: metricsRes.data?.inquiries_count || 0, 
            trend: metricsRes.data?.inquiries_trend || 0 
          },
          leads: { 
            total: leadsRes.data?.leads || 0, 
            new: leadsRes.data?.new_leads || 0 
          }
        });
      }
    } catch (e) {
      console.error('Error fetching stats:', e);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#F7F3EE] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F47A3B] animate-spin" />
      </div>
    );
  }

  const StatCard = ({ title, value, trend, icon: Icon, color }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-[rgba(17,24,39,0.10)] p-6 shadow-sm"
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        {trend !== 0 && (
          <Badge className={trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
            {trend > 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
            {Math.abs(trend)}%
          </Badge>
        )}
      </div>
      <p className="text-3xl font-bold text-zinc-900 mb-1">{value.toLocaleString()}</p>
      <p className="text-[var(--zinc-500, #71717a)] text-sm">{title}</p>
    </motion.div>
  );

  return (
    <PanelLayout>
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/15 mb-4">
          <BarChart3 className="w-3.5 h-3.5 text-[#F47A3B]" />
          <span className="text-[12px] font-semibold text-[#F47A3B]">Dane i wyniki</span>
        </div>
        <h1 className="text-3xl font-extrabold text-zinc-900 mb-2">Statystyki</h1>
        <p className="text-[15px] text-zinc-500 max-w-md mx-auto">Sprawdź jak działa Twoja promocja</p>
      </div>
      {/* Period selector */}
      <div className="flex items-center gap-3 mb-8">
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="bg-white border border-zinc-200 rounded-lg px-3 py-2 text-sm text-zinc-900"
        >
          <option value="7d">{t('statistics.last7Days')}</option>
          <option value="30d">{t('statistics.last30Days')}</option>
          <option value="90d">{t('statistics.last90Days')}</option>
        </select>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchStats}
          className="border-zinc-200"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          title={t('statistics.views')} 
          value={stats.views.total} 
          trend={stats.views.trend}
          icon={Eye}
          color="bg-blue-500"
        />
        <StatCard 
          title={t('statistics.clicks')} 
          value={stats.clicks.total} 
          trend={stats.clicks.trend}
          icon={MousePointer}
          color="bg-purple-500"
        />
        <StatCard 
          title={t('statistics.inquiries')} 
          value={stats.inquiries.total} 
          trend={stats.inquiries.trend}
          icon={Mail}
          color="bg-green-500"
        />
        <StatCard 
          title={t('statistics.leads')} 
          value={stats.leads.total} 
          trend={0}
          icon={Users}
          color="bg-[#F47A3B]"
        />
      </div>

      {/* Growth Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-2xl border border-[rgba(17,24,39,0.10)] p-6 shadow-sm"
      >
        <h2 className="text-lg font-semibold text-zinc-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-500" />
          {t('statistics.growthTips')}
        </h2>
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">1</span>
            </div>
            <div>
              <h3 className="font-medium text-zinc-900">{t('statistics.tip1Title')}</h3>
              <p className="text-zinc-600 text-sm">{t('statistics.tip1Desc')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-purple-50 rounded-xl border border-purple-100">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">2</span>
            </div>
            <div>
              <h3 className="font-medium text-zinc-900">{t('statistics.tip2Title')}</h3>
              <p className="text-zinc-600 text-sm">{t('statistics.tip2Desc')}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-4 bg-green-50 rounded-xl border border-green-100">
            <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm">3</span>
            </div>
            <div>
              <h3 className="font-medium text-zinc-900">{t('statistics.tip3Title')}</h3>
              <p className="text-zinc-600 text-sm">{t('statistics.tip3Desc')}</p>
            </div>
          </div>
        </div>
      </motion.div>
    </PanelLayout>
  );
}
